import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ShoppingBag,
  Package,
  Plus,
  Trash2,
  DollarSign,
  Tag,
  Image,
  Search,
  ShoppingCart,
  Shirt,
  LayoutDashboard,
  Percent,
  Receipt,
  Building2,
  User,
  Truck,
  ZoomIn,
  ZoomOut,
  Move,
  RotateCcw,
  X,
  Save,
} from "lucide-react";

// Import refactored components
import {
  OrdersManagement,
  ProductCard,
  CategoryFilter,
  PinProductDialog,
  WebshopStats,
} from "@/components/admin/webshop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VAT_RATE = 0.25;

const DEFAULT_CATEGORIES = [
  { id: "scaffolding", name: "Scaffolding", icon: "🏗️" },
  { id: "safety_gear", name: "Safety Gear", icon: "🦺" },
  { id: "clothing", name: "Work Clothing", icon: "👕" },
  { id: "footwear", name: "Footwear", icon: "👟" },
  { id: "tools", name: "Tools", icon: "🔧" },
  { id: "accessories", name: "Accessories", icon: "📦" },
  { id: "nets_tarps", name: "Nets & Tarps", icon: "🎯" },
];

const DRAFT_KEY = "webshop_product_draft";

export default function AdminWebshop() {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("products");
  const [hasDraft, setHasDraft] = useState(false);
  
  // Pin to top
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [productToPin, setProductToPin] = useState(null);
  const [pinDuration, setPinDuration] = useState("permanent");
  
  // Custom sizes for clothing and footwear
  const [customSizes, setCustomSizes] = useState(["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"]);
  const shoeSizes = ["35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48"];
  
  // Product form
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    sku: "",
    category: "scaffolding",
    categories: ["scaffolding"],  // Multiple categories
    price: "",
    price_ex_vat: "",
    stock_quantity: "",
    sizes: [],
    size_inventory: {},
    variants: [],  // Product variants [{name: "60cm", stock: 10, price_adjustment: 0}, ...]
    photos: [],
    image_settings: {}, // {url: {zoom: 1, position_x: 50, position_y: 50}}
    discount_enabled: false,
    discount_percent: "",
    discount_start_date: "",
    discount_end_date: "",
    discount_permanent: false,
    show_vat_prices: true,
    weight_kg: "",
    length_cm: "",
    width_cm: "",
    height_cm: ""
  });
  
  // New variant input
  const [newVariant, setNewVariant] = useState({ name: "", stock: "", price_adjustment: "0" });
  
  // Image editor state
  const [editingImageIndex, setEditingImageIndex] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchOrders();
    fetchCategories();
    checkForDraft();
  }, []);

  // Check for saved draft on mount
  const checkForDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        // Only show draft notification if it has meaningful content
        if (draft.name || draft.description || draft.price || (draft.photos && draft.photos.length > 0)) {
          setHasDraft(true);
        }
      }
    } catch (e) {
      console.error("Error checking draft:", e);
    }
  };

  // Auto-save draft when form changes
  useEffect(() => {
    if (dialogOpen && !editingProduct) {
      // Only save draft for new products, not when editing
      const hasContent = productForm.name || productForm.description || productForm.price || 
                         (productForm.photos && productForm.photos.length > 0) ||
                         (productForm.variants && productForm.variants.length > 0);
      if (hasContent) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(productForm));
      }
    }
  }, [productForm, dialogOpen, editingProduct]);

  // Restore draft
  const restoreDraft = () => {
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        setProductForm(draft);
        setDialogOpen(true);
        setHasDraft(false);
        toast.success("Draft restored!");
      }
    } catch (e) {
      toast.error("Failed to restore draft");
    }
  };

  // Clear draft
  const clearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
    toast.info("Draft discarded");
  };

  // Save draft manually
  const saveDraft = () => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(productForm));
    toast.success("Draft saved!");
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/shop/products`);
      setProducts(res.data);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/shop/orders`);
      setOrders(res.data);
    } catch (err) {
      console.error("Error fetching orders:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/shop/categories`);
      if (res.data && res.data.length > 0) {
        setCategories(res.data);
      }
    } catch (err) {
    }
  };

  const addCategory = async (newCategory) => {
    try {
      await axios.post(`${API}/shop/categories`, newCategory);
      setCategories(prev => [...prev, newCategory]);
      toast.success("Category added!");
    } catch (err) {
      setCategories(prev => [...prev, newCategory]);
      toast.success("Category added!");
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];
    
    for (const file of files) {
      try {
        // Get Cloudinary signature from backend
        const sigRes = await axios.get(`${API}/cloudinary/signature?folder=products&resource_type=image`);
        const { signature, timestamp, cloud_name, api_key, folder } = sigRes.data;
        
        // Upload directly to Cloudinary
        const formData = new FormData();
        formData.append("file", file);
        formData.append("api_key", api_key);
        formData.append("timestamp", timestamp);
        formData.append("signature", signature);
        formData.append("folder", folder);
        
        const cloudinaryRes = await fetch(
          `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
          { method: "POST", body: formData }
        );
        
        const cloudinaryData = await cloudinaryRes.json();
        
        if (cloudinaryData.secure_url) {
          uploadedUrls.push(cloudinaryData.secure_url);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setProductForm(prev => ({
      ...prev,
      photos: [...prev.photos, ...uploadedUrls]
    }));
    setUploading(false);
  };

  const addPhotoUrl = (url) => {
    if (url && url.startsWith("http")) {
      setProductForm(prev => ({
        ...prev,
        photos: [...prev.photos, url]
      }));
    }
  };

  const removePhoto = (index) => {
    const photoUrl = productForm.photos[index];
    setProductForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
      image_settings: Object.fromEntries(
        Object.entries(prev.image_settings).filter(([key]) => key !== photoUrl)
      )
    }));
  };

  // Image settings functions
  const getImageSettings = (url) => {
    return productForm.image_settings[url] || { zoom: 1, position_x: 50, position_y: 50 };
  };

  const updateImageSettings = (url, settings) => {
    setProductForm(prev => ({
      ...prev,
      image_settings: {
        ...prev.image_settings,
        [url]: { ...getImageSettings(url), ...settings }
      }
    }));
  };

  const resetImageSettings = (url) => {
    setProductForm(prev => ({
      ...prev,
      image_settings: {
        ...prev.image_settings,
        [url]: { zoom: 1, position_x: 50, position_y: 50 }
      }
    }));
  };

  const handleSizeToggle = (size) => {
    setProductForm(prev => {
      const newSizes = prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size];
      
      const newInventory = { ...prev.size_inventory };
      if (!prev.sizes.includes(size)) {
        newInventory[size] = 0;
      } else {
        delete newInventory[size];
      }
      
      return {
        ...prev,
        sizes: newSizes,
        size_inventory: newInventory
      };
    });
  };

  const updateSizeStock = (size, stock) => {
    setProductForm(prev => ({
      ...prev,
      size_inventory: {
        ...prev.size_inventory,
        [size]: parseInt(stock) || 0
      }
    }));
  };

  const getTotalSizeStock = () => {
    return Object.values(productForm.size_inventory).reduce((sum, qty) => sum + (qty || 0), 0);
  };

  const calculatePrices = (basePrice, isExVat = false) => {
    const price = parseFloat(basePrice) || 0;
    if (isExVat) {
      return { price_ex_vat: price, price_inc_vat: price * (1 + VAT_RATE) };
    }
    return { price_inc_vat: price, price_ex_vat: price / (1 + VAT_RATE) };
  };

  const getDiscountedPrice = (product) => {
    if (!product.discount_enabled) return null;
    
    const now = new Date();
    if (!product.discount_permanent) {
      if (product.discount_start_date && new Date(product.discount_start_date) > now) return null;
      if (product.discount_end_date && new Date(product.discount_end_date) < now) return null;
    }
    
    const discountPercent = product.discount_percent || 0;
    return product.price * (1 - discountPercent / 100);
  };

  const openPinDialog = (product) => {
    setProductToPin(product);
    setPinDuration(product.pinned ? "permanent" : "permanent");
    setPinDialogOpen(true);
  };

  const handlePinProduct = async () => {
    if (!productToPin) return;
    
    try {
      let pinnedUntil = null;
      const now = new Date();
      
      if (pinDuration === "1h") {
        pinnedUntil = new Date(now.getTime() + 60 * 60 * 1000).toISOString();
      } else if (pinDuration === "24h") {
        pinnedUntil = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString();
      } else if (pinDuration === "7d") {
        pinnedUntil = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      }
      
      await axios.put(`${API}/shop/products/${productToPin.product_id}`, {
        ...productToPin,
        pinned: true,
        pinned_until: pinnedUntil
      });
      
      toast.success(`${productToPin.name} pinned to top!`);
      fetchProducts();
      setPinDialogOpen(false);
      setProductToPin(null);
    } catch (error) {
      toast.error("Failed to pin product");
    }
  };

  const handleUnpinProduct = async (product) => {
    try {
      await axios.put(`${API}/shop/products/${product.product_id}`, {
        ...product,
        pinned: false,
        pinned_until: null
      });
      
      toast.success(`${product.name} unpinned`);
      fetchProducts();
    } catch (error) {
      toast.error("Failed to unpin product");
    }
  };

  const getSortedProducts = () => {
    const now = new Date();
    
    return [...products]
      .filter(p => {
        const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.sku?.toLowerCase().includes(searchTerm.toLowerCase());
        // Support both single category and multiple categories
        const matchesCategory = !selectedCategory || 
          p.category === selectedCategory || 
          (p.categories && p.categories.includes(selectedCategory));
        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => {
        const aIsPinned = a.pinned && (!a.pinned_until || new Date(a.pinned_until) > now);
        const bIsPinned = b.pinned && (!b.pinned_until || new Date(b.pinned_until) > now);
        const aHasDiscount = a.discount_enabled && a.discount_percent > 0;
        const bHasDiscount = b.discount_enabled && b.discount_percent > 0;
        
        if (aIsPinned && !bIsPinned) return -1;
        if (!aIsPinned && bIsPinned) return 1;
        if (aHasDiscount && !bHasDiscount) return -1;
        if (!aHasDiscount && bHasDiscount) return 1;
        if (aHasDiscount && bHasDiscount) {
          return (b.discount_percent || 0) - (a.discount_percent || 0);
        }
        return 0;
      });
  };

  const saveProduct = async () => {
    if (!productForm.name || !productForm.price) {
      toast.error("Name and price are required");
      return;
    }

    try {
      // price_ex_vat is the input (without VAT), price is calculated (with VAT)
      const priceExVat = parseFloat(productForm.price_ex_vat) || 0;
      const priceIncVat = priceExVat * (1 + VAT_RATE);
      
      let totalStock = parseInt(productForm.stock_quantity) || 0;
      
      // Calculate total stock from size inventory if clothing or footwear
      const hasClothingSizes = productForm.categories?.includes("clothing") && Object.keys(productForm.size_inventory).length > 0;
      const hasFootwearSizes = productForm.categories?.includes("footwear") && Object.keys(productForm.size_inventory).length > 0;
      
      if ((hasClothingSizes || hasFootwearSizes) && productForm.sizes.length > 0) {
        totalStock = getTotalSizeStock();
      }
      
      // If variants exist, use sum of variant stocks as total (overrides any other calculation)
      if (productForm.variants && productForm.variants.length > 0) {
        totalStock = productForm.variants.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0);
      }
      
      const data = {
        ...productForm,
        category: productForm.categories?.[0] || productForm.category,  // Keep first category as primary for backwards compat
        categories: productForm.categories || [productForm.category],
        price: priceIncVat,  // Price with VAT (for display to consumers)
        price_ex_vat: priceExVat,  // Price without VAT (for B2B)
        stock_quantity: totalStock,
        size_inventory: productForm.size_inventory,
        variants: productForm.variants || [],
        image_settings: productForm.image_settings,
        discount_percent: productForm.discount_enabled ? parseFloat(productForm.discount_percent) || 0 : 0,
        discount_enabled: productForm.discount_enabled,
        discount_permanent: productForm.discount_permanent,
        discount_start_date: productForm.discount_start_date || null,
        discount_end_date: productForm.discount_end_date || null,
        weight_kg: parseFloat(productForm.weight_kg) || 0,
        length_cm: parseFloat(productForm.length_cm) || 0,
        width_cm: parseFloat(productForm.width_cm) || 0,
        height_cm: parseFloat(productForm.height_cm) || 0
      };

      if (editingProduct) {
        await axios.put(`${API}/shop/products/${editingProduct.product_id}`, data);
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/shop/products`, data);
        toast.success("Product created!");
      }
      
      setDialogOpen(false);
      resetForm();
      // Small delay to ensure DB is updated before fetching
      setTimeout(() => fetchProducts(), 300);
    } catch (err) {
      toast.error("Failed to save product");
    }
  };

  const deleteProduct = async (productId, e) => {
    e?.stopPropagation();
    if (!confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/shop/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (err) {
      toast.error("Failed to delete product");
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    // If price_ex_vat exists, use it. Otherwise calculate from price
    const priceExVat = product.price_ex_vat || (product.price / (1 + VAT_RATE));
    const priceIncVat = product.price || (priceExVat * (1 + VAT_RATE));
    
    setProductForm({
      name: product.name,
      description: product.description || "",
      sku: product.sku || "",
      category: product.category,
      categories: product.categories || [product.category],
      price: priceIncVat?.toFixed(2) || "",
      price_ex_vat: priceExVat?.toFixed(2) || "",
      stock_quantity: product.stock_quantity?.toString() || "0",
      sizes: product.sizes || [],
      size_inventory: product.size_inventory || {},
      variants: product.variants || [],
      photos: product.photos || [],
      image_settings: product.image_settings || {},
      discount_enabled: product.discount_enabled || false,
      discount_percent: product.discount_percent?.toString() || "",
      discount_start_date: product.discount_start_date || "",
      discount_end_date: product.discount_end_date || "",
      discount_permanent: product.discount_permanent || false,
      show_vat_prices: true,
      weight_kg: product.weight_kg?.toString() || "",
      length_cm: product.length_cm?.toString() || "",
      width_cm: product.width_cm?.toString() || "",
      height_cm: product.height_cm?.toString() || ""
    });
    setEditingImageIndex(null);
    setNewVariant({ name: "", stock: "", price_adjustment: "0" });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setEditingImageIndex(null);
    setNewVariant({ name: "", stock: "", price_adjustment: "0" });
    // Clear draft when resetting after successful save
    localStorage.removeItem(DRAFT_KEY);
    setHasDraft(false);
    setProductForm({
      name: "",
      description: "",
      sku: "",
      category: "scaffolding",
      categories: ["scaffolding"],
      price: "",
      price_ex_vat: "",
      stock_quantity: "",
      sizes: [],
      size_inventory: {},
      variants: [],
      photos: [],
      image_settings: {},
      discount_enabled: false,
      discount_percent: "",
      discount_start_date: "",
      discount_end_date: "",
      discount_permanent: false,
      show_vat_prices: true,
      weight_kg: "",
      length_cm: "",
      width_cm: "",
      height_cm: ""
    });
  };

  const seedSampleProducts = async () => {
    const sampleProducts = [
      {
        name: "Stillaspresenning Ankerstropp 310mm (100 stk)",
        description: "Elastiske ankerstropper 310 mm – sikker og enkel montering av stillaspressenning (100 stk)",
        category: "accessories",
        price: 295,
        stock_quantity: 50,
        photos: ["https://www.stillaser.com/_next/image?url=https%3A%2F%2Futfs.io%2Ff%2F3e56c2e1-d3c2-455a-a0ee-65ffaff217a9-n4ly78.png&w=640&q=75"]
      },
      {
        name: "Plattform med luke og stige, aluminium/finer – 3,07m",
        description: "Plattform 3,07 m i aluminium/finèr med luke og stige. Sikker, robust og enkel å montere på rammestillas.",
        category: "scaffolding",
        price: 1690,
        stock_quantity: 25,
        photos: ["https://www.stillaser.com/_next/image?url=https%3A%2F%2Futfs.io%2Ff%2F1e5b1c09-f533-409d-8e0d-e6545a7f7a68-ppddfp.png&w=640&q=75"]
      },
      {
        name: "Wenaas Arbeidsjakke Bomull",
        description: "Arbeidsjakke fra Wenaas i bomull. Komfortabel og slitesterk.",
        category: "clothing",
        price: 99,
        stock_quantity: 100,
        sizes: ["S", "M", "L", "XL", "XXL"],
        discount_enabled: true,
        discount_percent: 30,
        discount_permanent: true,
        photos: ["https://www.stillaser.com/_next/image?url=https%3A%2F%2Futfs.io%2Ff%2Fbd9d9987-4b69-4cb5-90bc-36a7f2c5982b-4vmiqd.jfif&w=640&q=75"]
      }
    ];

    let added = 0;
    for (const product of sampleProducts) {
      try {
        const prices = calculatePrices(product.price, false);
        await axios.post(`${API}/shop/products`, { ...product, price_ex_vat: prices.price_ex_vat });
        added++;
      } catch (err) {
        console.error("Error adding product:", err);
      }
    }
    
    toast.success(`Added ${added} sample products!`);
    fetchProducts();
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Webshop Admin">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title="Webshop Admin">
      <div className="space-y-6">
        {/* Stats */}
        <WebshopStats products={products} />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border-white/10">
            <TabsTrigger value="products" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <Package className="w-4 h-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Orders ({orders.length})
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-[#23F7DD]" />
                    Products
                  </CardTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search products..."
                        className="pl-9 bg-black/50 border-white/10 text-white"
                      />
                    </div>
                    <Button 
                      onClick={() => { resetForm(); setDialogOpen(true); }}
                      className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                      data-testid="add-product-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Product
                    </Button>
                  </div>
                </div>
                
                {/* Draft Recovery Banner */}
                {hasDraft && (
                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Save className="w-5 h-5 text-yellow-400" />
                      <p className="text-sm text-yellow-300">
                        You have an unsaved product draft. Would you like to continue working on it?
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={restoreDraft}
                        size="sm"
                        className="bg-yellow-500 text-black hover:bg-yellow-400"
                      >
                        Restore Draft
                      </Button>
                      <Button 
                        onClick={clearDraft}
                        size="sm"
                        variant="outline"
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      >
                        Discard
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Category Filter */}
                <CategoryFilter
                  categories={categories}
                  setCategories={setCategories}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  products={products}
                  onAddCategory={addCategory}
                />
              </CardHeader>
              <CardContent>
                {/* VAT Info Banner */}
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-blue-400" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-300">
                      <strong>MVA/VAT:</strong> Prices include 25% Norwegian VAT. 
                      <span className="text-[#94A3B8] ml-2">
                        Companies (B2B) see ex. VAT | Employees & Private see inc. VAT
                      </span>
                    </p>
                  </div>
                </div>

                {getSortedProducts().length === 0 ? (
                  <div className="text-center py-12 text-[#94A3B8]">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No products found</p>
                    <p className="text-sm mt-1">Add your first product or seed demo products</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {getSortedProducts().map((product) => (
                      <ProductCard
                        key={product.product_id}
                        product={product}
                        categories={categories}
                        onEdit={openEditDialog}
                        onDelete={deleteProduct}
                        onPin={openPinDialog}
                        onUnpin={handleUnpinProduct}
                        getDiscountedPrice={getDiscountedPrice}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <OrdersManagement orders={orders} onRefresh={fetchOrders} />
          </TabsContent>
        </Tabs>

        {/* Product Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[95vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded']">
                {editingProduct ? "Edit Product" : "Add New Product"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Product Name *</label>
                  <Input
                    value={productForm.name}
                    onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Fallsikringssele P 70"
                    className="bg-black/50 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                    <Tag className="w-3 h-3" /> SKU / Product Code
                  </label>
                  <Input
                    value={productForm.sku}
                    onChange={(e) => setProductForm(prev => ({ ...prev, sku: e.target.value.toUpperCase() }))}
                    placeholder="e.g., XS-HARNESS-001"
                    className="bg-black/50 border-white/10 text-white font-mono"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block">Description</label>
                <Textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Product description..."
                  className="bg-black/50 border-white/10 text-white"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Categories (select multiple)</label>
                  <div className="flex flex-wrap gap-2 p-2 bg-black/50 border border-white/10 rounded-lg min-h-[42px]">
                    {categories.map(cat => {
                      const isSelected = productForm.categories?.includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setProductForm(prev => {
                              const current = prev.categories || [];
                              if (isSelected) {
                                // Don't allow removing last category
                                if (current.length <= 1) return prev;
                                return { ...prev, categories: current.filter(c => c !== cat.id), category: current.filter(c => c !== cat.id)[0] };
                              } else {
                                return { ...prev, categories: [...current, cat.id], category: current[0] || cat.id };
                              }
                            });
                          }}
                          className={`px-2 py-1 rounded text-xs transition-all ${
                            isSelected 
                              ? 'bg-[#23F7DD] text-black font-medium' 
                              : 'bg-white/10 text-[#94A3B8] hover:bg-white/20'
                          }`}
                        >
                          {cat.icon} {cat.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">
                    Stock Quantity
                    {productForm.variants?.length > 0 && (
                      <span className="text-xs text-[#64748B] ml-2">(auto-calculated from variants)</span>
                    )}
                  </label>
                  {productForm.variants?.length > 0 ? (
                    <div className="bg-black/50 border border-white/10 rounded-lg p-2 text-white">
                      <span className="text-[#23F7DD] font-bold">
                        {productForm.variants.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0)}
                      </span>
                      <span className="text-[#64748B] text-sm ml-2">
                        (sum of all variants)
                      </span>
                    </div>
                  ) : (
                    <Input
                      type="number"
                      value={productForm.stock_quantity}
                      onChange={(e) => setProductForm(prev => ({ ...prev, stock_quantity: e.target.value }))}
                      placeholder="50"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  )}
                </div>
              </div>

              {/* Product Variants Section */}
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#23F7DD]" />
                  Product Variants (Sizes/Options)
                </h4>
                <p className="text-xs text-[#64748B] mb-3">
                  Add variants like sizes (60cm, 80cm, 1M) or options. <br/>
                  <span className="text-yellow-400">⚠️ When using variants, the total stock is the SUM of all variant stocks.</span>
                </p>
                
                {/* Existing variants - EDITABLE */}
                {productForm.variants?.length > 0 && (
                  <div className="space-y-2 mb-3">
                    <p className="text-xs text-[#94A3B8] mb-2">Current variants:</p>
                    {productForm.variants.map((variant, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-black/30 rounded">
                        <span className="text-white font-medium min-w-[80px]">{variant.name}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-[#64748B] text-xs">Stock:</span>
                          <Input
                            type="number"
                            min="0"
                            value={variant.stock}
                            onChange={(e) => {
                              const newStock = parseInt(e.target.value) || 0;
                              setProductForm(prev => ({
                                ...prev,
                                variants: prev.variants.map((v, i) => 
                                  i === idx ? { ...v, stock: newStock } : v
                                )
                              }));
                            }}
                            className="bg-black/50 border-white/10 text-white w-20 h-8 text-center"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[#64748B] text-xs">+/-:</span>
                          <Input
                            type="number"
                            value={variant.price_adjustment || 0}
                            onChange={(e) => {
                              const newAdj = Math.round(parseFloat(e.target.value) || 0);
                              setProductForm(prev => ({
                                ...prev,
                                variants: prev.variants.map((v, i) => 
                                  i === idx ? { ...v, price_adjustment: newAdj } : v
                                )
                              }));
                            }}
                            className="bg-black/50 border-white/10 text-white w-20 h-8 text-center"
                          />
                          <span className="text-[#64748B] text-xs">NOK</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setProductForm(prev => ({
                              ...prev,
                              variants: prev.variants.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="text-red-400 hover:text-red-300 p-1 ml-auto"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add new variant */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Variant name (e.g., 60cm, XL)"
                    value={newVariant.name}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-black/50 border-white/10 text-white flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Stock"
                    value={newVariant.stock}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, stock: e.target.value }))}
                    className="bg-black/50 border-white/10 text-white w-20"
                  />
                  <Input
                    type="number"
                    placeholder="+/- NOK"
                    value={newVariant.price_adjustment}
                    onChange={(e) => setNewVariant(prev => ({ ...prev, price_adjustment: e.target.value }))}
                    className="bg-black/50 border-white/10 text-white w-24"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      if (!newVariant.name.trim()) return;
                      setProductForm(prev => ({
                        ...prev,
                        variants: [...(prev.variants || []), {
                          name: newVariant.name,
                          stock: parseInt(newVariant.stock) || 0,
                          price_adjustment: Math.round(parseFloat(newVariant.price_adjustment) || 0)
                        }]
                      }));
                      setNewVariant({ name: "", stock: "", price_adjustment: "0" });
                    }}
                    className="bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#23F7DD]" />
                  Pricing (25% MVA/VAT)
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Price ex. VAT (NOK) *</label>
                    <Input
                      type="number"
                      value={productForm.price_ex_vat}
                      onChange={(e) => {
                        const val = e.target.value;
                        const exVat = parseFloat(val) || 0;
                        const incVat = exVat * (1 + VAT_RATE);
                        setProductForm(prev => ({ 
                          ...prev, 
                          price_ex_vat: val,
                          price: incVat.toFixed(2)
                        }));
                      }}
                      placeholder="1200"
                      className="bg-black/50 border-white/10 text-white"
                    />
                    <p className="text-xs text-[#64748B] mt-1 flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Companies (B2B)
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Price inc. VAT (NOK)</label>
                    <Input
                      type="number"
                      value={productForm.price}
                      readOnly
                      className="bg-black/30 border-white/10 text-[#94A3B8]"
                    />
                    <p className="text-xs text-[#64748B] mt-1 flex items-center gap-1">
                      <User className="w-3 h-3" /> Private & Employees
                    </p>
                  </div>
                </div>
              </div>

              {/* Discount Section */}
              <div className="p-4 bg-pink-500/10 rounded-lg border border-pink-500/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <Percent className="w-4 h-4 text-pink-400" />
                    Discount
                  </h4>
                  <Switch
                    checked={productForm.discount_enabled}
                    onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, discount_enabled: checked }))}
                  />
                </div>
                
                {productForm.discount_enabled && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-[#94A3B8] mb-1 block">Discount %</label>
                      <Input
                        type="number"
                        value={productForm.discount_percent}
                        onChange={(e) => setProductForm(prev => ({ ...prev, discount_percent: e.target.value }))}
                        placeholder="30"
                        max="100"
                        className="bg-black/50 border-white/10 text-white"
                      />
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={productForm.discount_permanent}
                        onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, discount_permanent: checked }))}
                      />
                      <label className="text-sm text-[#94A3B8]">Permanent discount (no expiry)</label>
                    </div>
                    
                    {!productForm.discount_permanent && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block">Start Date</label>
                          <Input
                            type="date"
                            value={productForm.discount_start_date}
                            onChange={(e) => setProductForm(prev => ({ ...prev, discount_start_date: e.target.value }))}
                            className="bg-black/50 border-white/10 text-white"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-[#94A3B8] mb-1 block">End Date</label>
                          <Input
                            type="date"
                            value={productForm.discount_end_date}
                            onChange={(e) => setProductForm(prev => ({ ...prev, discount_end_date: e.target.value }))}
                            className="bg-black/50 border-white/10 text-white"
                          />
                        </div>
                      </div>
                    )}
                    
                    {productForm.price_ex_vat && productForm.discount_percent && (
                      <div className="p-3 bg-black/30 rounded space-y-2">
                        {/* Price with VAT (for private/employees) */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-xs text-[#94A3B8]">
                            <User className="w-3 h-3" /> Inc. VAT:
                          </div>
                          <div>
                            <span className="text-[#23F7DD] font-bold">
                              {(productForm.price * (1 - productForm.discount_percent / 100)).toFixed(0)} NOK
                            </span>
                            <span className="text-red-400 line-through ml-2 text-sm">{parseFloat(productForm.price).toFixed(0)} NOK</span>
                          </div>
                        </div>
                        {/* Price without VAT (for B2B) */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 text-xs text-[#64748B]">
                            <Building2 className="w-3 h-3" /> Ex. VAT:
                          </div>
                          <div>
                            <span className="text-blue-400 font-bold">
                              {(parseFloat(productForm.price_ex_vat) * (1 - productForm.discount_percent / 100)).toFixed(0)} NOK
                            </span>
                            <span className="text-red-400/70 line-through ml-2 text-sm">{parseFloat(productForm.price_ex_vat).toFixed(0)} NOK</span>
                          </div>
                        </div>
                        {/* Savings */}
                        <div className="text-center pt-2 border-t border-white/10">
                          <span className="text-green-400 text-sm">
                            💰 Save {(parseFloat(productForm.price) * productForm.discount_percent / 100).toFixed(0)} NOK ({productForm.discount_percent}%)
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Shipping Details */}
              <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-orange-400" />
                  Shipping Details
                  <span className="text-xs text-[#64748B]">(for Bring price calculation)</span>
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Weight (kg)</label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      value={productForm.weight_kg}
                      onChange={(e) => setProductForm(prev => ({ ...prev, weight_kg: e.target.value }))}
                      placeholder="0.5"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Length (cm)</label>
                    <Input
                      type="number"
                      min="0"
                      value={productForm.length_cm}
                      onChange={(e) => setProductForm(prev => ({ ...prev, length_cm: e.target.value }))}
                      placeholder="30"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Width (cm)</label>
                    <Input
                      type="number"
                      min="0"
                      value={productForm.width_cm}
                      onChange={(e) => setProductForm(prev => ({ ...prev, width_cm: e.target.value }))}
                      placeholder="20"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Height (cm)</label>
                    <Input
                      type="number"
                      min="0"
                      value={productForm.height_cm}
                      onChange={(e) => setProductForm(prev => ({ ...prev, height_cm: e.target.value }))}
                      placeholder="10"
                      className="bg-black/50 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Sizes with Inventory - Clothing */}
              {productForm.categories?.includes("clothing") && (
                <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Shirt className="w-4 h-4 text-blue-400" />
                    Clothing Size Management
                  </h4>
                  <p className="text-xs text-[#94A3B8] mb-3">
                    Select sizes and set individual stock for each. Total stock: <span className="text-[#23F7DD] font-bold">{getTotalSizeStock()}</span>
                  </p>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {customSizes.map(size => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => handleSizeToggle(size)}
                          className={`px-3 py-1 rounded border transition-all ${
                            productForm.sizes.includes(size)
                              ? "bg-[#23F7DD] border-[#23F7DD] text-[#050505] font-medium"
                              : "border-white/20 text-[#94A3B8] hover:border-[#23F7DD]"
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                    
                    {productForm.sizes.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-white/10">
                        {productForm.sizes.map(size => (
                          <div key={size} className="bg-black/30 rounded-lg p-3">
                            <label className="text-xs text-[#94A3B8] block mb-1">Size {size}</label>
                            <Input
                              type="number"
                              min="0"
                              value={productForm.size_inventory[size] || 0}
                              onChange={(e) => updateSizeStock(size, e.target.value)}
                              className="bg-black/50 border-white/10 text-white text-center h-10"
                              placeholder="0"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Sizes with Inventory - Footwear */}
              {productForm.categories?.includes("footwear") && (
                <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <span className="text-lg">👟</span>
                    Shoe Size Management (EU)
                  </h4>
                  <p className="text-xs text-[#94A3B8] mb-3">
                    Select shoe sizes and set individual stock for each. Total stock: <span className="text-[#23F7DD] font-bold">{getTotalSizeStock()}</span>
                  </p>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {shoeSizes.map(size => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => handleSizeToggle(size)}
                          className={`px-3 py-1 rounded border transition-all ${
                            productForm.sizes.includes(size)
                              ? "bg-orange-500 border-orange-500 text-white font-medium"
                              : "border-white/20 text-[#94A3B8] hover:border-orange-500"
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                    
                    {productForm.sizes.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2 mt-4 pt-4 border-t border-white/10">
                        {productForm.sizes.map(size => (
                          <div key={size} className="bg-black/30 rounded-lg p-2">
                            <label className="text-xs text-[#94A3B8] block mb-1 text-center">{size}</label>
                            <Input
                              type="number"
                              min="0"
                              value={productForm.size_inventory[size] || 0}
                              onChange={(e) => updateSizeStock(size, e.target.value)}
                              className="bg-black/50 border-white/10 text-white text-center h-8 text-sm"
                              placeholder="0"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Images */}
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block">Product Images</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {productForm.photos.map((url, index) => {
                    const settings = getImageSettings(url);
                    const isEditing = editingImageIndex === index;
                    
                    return (
                      <div key={index} className={`relative ${isEditing ? 'w-full' : 'w-20 h-20'}`}>
                        {isEditing ? (
                          // Image Editor View
                          <div className="bg-black/30 rounded-lg p-4 border border-[#23F7DD]/30">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-white text-sm font-medium">Adjust Image</span>
                              <button
                                type="button"
                                onClick={() => setEditingImageIndex(null)}
                                className="text-[#94A3B8] hover:text-white"
                              >
                                Done
                              </button>
                            </div>
                            
                            {/* Preview with zoom/position */}
                            <div className="w-full aspect-square bg-white rounded-lg overflow-hidden mb-3 relative">
                              <img 
                                src={url} 
                                alt="" 
                                className="absolute"
                                style={{
                                  width: `${settings.zoom * 100}%`,
                                  height: `${settings.zoom * 100}%`,
                                  left: `${settings.position_x - (settings.zoom * 50)}%`,
                                  top: `${settings.position_y - (settings.zoom * 50)}%`,
                                  objectFit: 'contain'
                                }}
                              />
                            </div>
                            
                            {/* Zoom Control */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-[#94A3B8] flex items-center gap-1">
                                  <ZoomIn className="w-3 h-3" /> Zoom
                                </span>
                                <span className="text-xs text-[#23F7DD]">{Math.round(settings.zoom * 100)}%</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <ZoomOut className="w-4 h-4 text-[#94A3B8]" />
                                <input
                                  type="range"
                                  min="0.5"
                                  max="2"
                                  step="0.05"
                                  value={settings.zoom}
                                  onChange={(e) => updateImageSettings(url, { zoom: parseFloat(e.target.value) })}
                                  className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#23F7DD]"
                                />
                                <ZoomIn className="w-4 h-4 text-[#94A3B8]" />
                              </div>
                            </div>
                            
                            {/* Position Controls */}
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div>
                                <span className="text-xs text-[#94A3B8] block mb-1">Horizontal</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={settings.position_x}
                                  onChange={(e) => updateImageSettings(url, { position_x: parseInt(e.target.value) })}
                                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#23F7DD]"
                                />
                              </div>
                              <div>
                                <span className="text-xs text-[#94A3B8] block mb-1">Vertical</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={settings.position_y}
                                  onChange={(e) => updateImageSettings(url, { position_y: parseInt(e.target.value) })}
                                  className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#23F7DD]"
                                />
                              </div>
                            </div>
                            
                            {/* Reset Button */}
                            <button
                              type="button"
                              onClick={() => resetImageSettings(url)}
                              className="flex items-center gap-1 text-xs text-[#94A3B8] hover:text-white"
                            >
                              <RotateCcw className="w-3 h-3" /> Reset to default
                            </button>
                          </div>
                        ) : (
                          // Thumbnail View
                          <div 
                            className="w-20 h-20 rounded-lg overflow-hidden bg-white cursor-pointer hover:ring-2 hover:ring-[#23F7DD] transition-all"
                            onClick={() => setEditingImageIndex(index)}
                          >
                            <img 
                              src={url} 
                              alt="" 
                              className="w-full h-full"
                              style={{
                                objectFit: 'cover',
                                objectPosition: `${settings.position_x}% ${settings.position_y}%`,
                                transform: `scale(${settings.zoom})`
                              }}
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity">
                              <Move className="w-5 h-5 text-white" />
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); removePhoto(index); }}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center z-10"
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-[#94A3B8] mb-2">Click on image to adjust zoom & position. White background fills empty space.</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste image URL..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addPhotoUrl(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="bg-black/50 border-white/10 text-white flex-1"
                  />
                  <label className="flex items-center justify-center gap-2 px-4 border border-dashed border-white/20 rounded-lg cursor-pointer hover:border-[#23F7DD]/50">
                    <Image className="w-5 h-5 text-[#94A3B8]" />
                    <span className="text-[#94A3B8] text-sm">{uploading ? "..." : "Upload"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>
              
              <div className="flex gap-2">
                {!editingProduct && (
                  <Button
                    onClick={saveDraft}
                    variant="outline"
                    className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Draft
                  </Button>
                )}
                <Button
                  onClick={saveProduct}
                  className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                >
                  {editingProduct ? "Update Product" : "Create Product"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pin to Top Dialog */}
        <PinProductDialog
          open={pinDialogOpen}
          onOpenChange={setPinDialogOpen}
          product={productToPin}
          pinDuration={pinDuration}
          setPinDuration={setPinDuration}
          onPinProduct={handlePinProduct}
        />
      </div>
    </DashboardLayout>
  );
}
